<template>
  <div class="login-container">
    <div class="login-card">
      <div class="login-header">
        <div class="logo">
          <el-icon :size="48"><VideoPlay /></el-icon>
        </div>
        <h1 class="title">视核云听</h1>
        <p class="subtitle">videocore</p>
      </div>

      <el-form
        ref="loginFormRef"
        :model="loginForm"
        :rules="loginRules"
        class="login-form"
        label-position="top"
        @keyup.enter="handleLogin"
      >
        <el-form-item prop="username" label="账号名称" class="login-form-item">
          <el-input
            v-model="loginForm.username"
            placeholder="请输入账号名称"
            size="large"
            class="login-input"
          >
            <template #prefix>
              <el-icon><User /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item prop="password" label="密码" class="login-form-item">
          <el-input
            v-model="loginForm.password"
            type="password"
            placeholder="请输入登录密码"
            size="large"
            show-password
            class="login-input"
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item>
          <div class="login-options">
            <el-checkbox v-model="rememberMe">记住我</el-checkbox>
            <el-link type="primary" :underline="false">忘记密码？</el-link>
          </div>
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            size="large"
            :loading="loading"
            class="login-button"
            @click="handleLogin"
          >
            {{ loading ? '登录中...' : '登录' }}
          </el-button>
        </el-form-item>

        <div class="divider">
          <span>或</span>
        </div>

        <div class="register-link">
          暂时没有开放注册。
          <el-link type="primary" @click="notifyRegistrationUnavailable">注册</el-link>
        </div>

      </el-form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import { User, Lock, VideoPlay } from '@element-plus/icons-vue'
import { useUserStore } from '@/stores/user'
import { login } from '@/api/auth'
import { notifyRegistrationUnavailable, resolvePostLoginRedirect } from '@/utils/authNavigation'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

const loginFormRef = ref<FormInstance>()
const loading = ref(false)
const rememberMe = ref(false)

const loginForm = reactive({
  username: '',
  password: '',
})

const loginRules: FormRules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
    { min: 3, max: 20, message: '用户名长度在 3-20 个字符', trigger: 'blur' },
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, message: '密码长度至少 6位', trigger: 'blur' },
  ],
}

const handleLogin = async () => {
  if (!loginFormRef.value) return

  await loginFormRef.value.validate(async (valid) => {
    if (!valid) return

    loading.value = true

    try {
      const response = await login(loginForm)
      userStore.setToken(response.access_token)
      userStore.setUserInfo(response.user)

      if (rememberMe.value) {
        localStorage.setItem('rememberedUsername', loginForm.username)
      } else {
        localStorage.removeItem('rememberedUsername')
      }
      
      ElMessage.success('登录成功！')
      
      const fallback = userStore.canAccessAdmin ? '/admin' : '/dashboard'
      router.push(resolvePostLoginRedirect(route.query.redirect, fallback))
    } catch (error: any) {
      ElMessage.error(error.message || '登录失败，请检查账号密码')
    } finally {
      loading.value = false
    }
  })
}

// Load remembered username
if (localStorage.getItem('rememberedUsername')) {
  loginForm.username = localStorage.getItem('rememberedUsername') || ''
  rememberMe.value = true
}

if (typeof route.query.username === 'string' && route.query.username.trim()) {
  loginForm.username = route.query.username.trim()
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at top, rgba(255, 255, 255, 0.36), transparent 28%),
    linear-gradient(140deg, #4361ee 0%, #5e60ce 42%, #6930c3 100%);
  padding: 24px;
}

.login-card {
  width: 100%;
  max-width: 420px;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 28px;
  padding: 48px 40px;
  box-shadow: 0 24px 80px rgba(35, 39, 96, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
}

.login-header {
  text-align: center;
  margin-bottom: 40px;
}

.logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #4361ee 0%, #6930c3 100%);
  border-radius: 50%;
  margin-bottom: 20px;
  color: white;
  box-shadow: 0 16px 32px rgba(86, 74, 196, 0.28);
}

.title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.subtitle {
  font-size: 15px;
  color: #6b7280;
  margin: 0;
}

.login-form {
  width: 100%;
}

.login-form-item {
  margin-bottom: 22px;
}

.login-form :deep(.el-form-item__label) {
  margin-bottom: 10px;
  color: #344054;
  font-weight: 600;
}

.login-input :deep(.el-input__wrapper) {
  min-height: 54px;
  padding: 0 18px;
  border-radius: 16px;
  background: #f8fafc;
  box-shadow: inset 0 0 0 1px #d7def0;
  transition: box-shadow 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
}

.login-input :deep(.el-input__wrapper.is-focus) {
  background: #ffffff;
  box-shadow: inset 0 0 0 1px #4361ee, 0 0 0 4px rgba(67, 97, 238, 0.12);
}

.login-input :deep(.el-input__prefix) {
  margin-right: 10px;
  color: #94a3b8;
  font-size: 18px;
}

.login-input :deep(.el-input__inner) {
  color: #111827;
  font-size: 16px;
}

.login-input :deep(.el-input__inner::placeholder) {
  color: #98a2b3;
}

.login-options {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.login-button {
  width: 100%;
  height: 52px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 16px;
  background: linear-gradient(135deg, #4361ee 0%, #3a86ff 100%);
  box-shadow: 0 18px 30px rgba(58, 134, 255, 0.24);
}

.login-button:hover {
  transform: translateY(-1px);
}

.divider {
  position: relative;
  text-align: center;
  margin: 24px 0;
}

.divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  border-top: 1px solid #e0e0e0;
}

.divider span {
  position: relative;
  background: white;
  padding: 0 16px;
  color: #999;
  font-size: 14px;
}

.register-link {
  text-align: center;
  font-size: 14px;
  color: #666;
}

.demo-accounts {
  margin-top: 32px;
  padding: 18px;
  background: linear-gradient(180deg, #f8faff 0%, #eef3ff 100%);
  border-radius: 18px;
  border: 1px solid #dde7ff;
}

.demo-accounts .el-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.demo-account {
  cursor: pointer;
  padding: 4px 0;
}

.login-options :deep(.el-checkbox__label) {
  color: #475467;
}

@media (max-width: 640px) {
  .login-card {
    padding: 36px 22px;
    border-radius: 24px;
  }

  .title {
    font-size: 24px;
  }
}
</style>
